import type { ComboboxItem, ComboboxItemGroup } from '@mantine/core';
import {
  getLocationOfServiceOptions,
  hospitalOptions,
} from '@billing/validation';
import {
  ApplyField,
  AssertionField,
  ScopeMode,
} from '@billing/validation/rules';
import {
  getServiceLocationIndicatorOptions,
  getClaimSelectOptions,
} from 'constants/claimFieldOptions';
import { getSpecialtyCodes } from 'constants/specialtyCodes';
import type { ClaimFieldOption } from 'constants/tariffConstants';
import type { RuleTranslate } from './constants';
import {
  selectableScopeModes,
  historyCriteriaFields,
  assertionFields,
  timeBasedIntervalOptions,
  dayOptions,
  whenOperatorOptions,
  patientAgeOperatorOptions,
  patientSexOptions,
  patientMaritalStatusOptions,
} from './constants';
import { provinceOptions } from 'constants/provinceConstants';

export interface RuleClaimFieldOption extends ClaimFieldOption {
  claimValue?: string;
  province?: string;
}

const LEGACY_CLAIM_VALUE_PREFIX = 'legacy:';

const getProvincialOptions = (
  getOptions: (province: 'MB' | 'BC' | 'SK') => readonly ClaimFieldOption[],
  provinces: readonly ('MB' | 'BC' | 'SK')[] = ['MB', 'BC', 'SK']
): RuleClaimFieldOption[] =>
  provinces.flatMap((province) =>
    getOptions(province).map((option) => ({
      ...option,
      value: `${province}:${option.value}`,
      claimValue: option.value,
      province,
    }))
  );

export const getRuleClaimFieldOptions = (
  field: string,
  translate: RuleTranslate
): RuleClaimFieldOption[] | undefined => {
  switch (field) {
    case ScopeMode.SpecialtyCode:
      return getProvincialOptions(
        (province) =>
          getSpecialtyCodes(province).map(({ code, description }) => ({
            value: code,
            label: `${code} - ${description}`,
          })),
        ['MB', 'BC']
      );
    case ApplyField.LocationOfService:
      return getProvincialOptions((province) =>
        getLocationOfServiceOptions({ province })
      );
    case ApplyField.ServiceLocationIndicator:
      return getProvincialOptions((province) =>
        getServiceLocationIndicatorOptions(province, translate)
      );
    case ApplyField.HospitalCode:
    case ApplyField.HospitalName:
      return hospitalOptions;
    case ApplyField.VirtualPhysicianRegion:
    case ApplyField.OptometristReason:
    case ApplyField.ThirdPartyLiability:
    case ApplyField.Incorporated:
      return getClaimSelectOptions(field, translate);
    default:
      return undefined;
  }
};

export const getRuleClaimOptionValue = (option: RuleClaimFieldOption): string =>
  option.claimValue ?? option.value;

export const getRuleClaimSelectData = (
  options: RuleClaimFieldOption[],
  values: string[]
): (ComboboxItem | ComboboxItemGroup)[] => {
  const data: (ComboboxItem | ComboboxItemGroup)[] = [];
  const groups = new Map<string, ComboboxItemGroup>();
  for (const option of options) {
    if (!option.province) {
      data.push(option);
      continue;
    }
    const group = groups.get(option.province);
    if (group) {
      group.items.push(option);
      continue;
    }
    const newGroup = { group: option.province, items: [option] };
    groups.set(option.province, newGroup);
    data.push(newGroup);
  }
  for (const value of new Set(values)) {
    if (
      value &&
      !options.some((option) => getRuleClaimOptionValue(option) === value)
    ) {
      data.push({
        value: `${LEGACY_CLAIM_VALUE_PREFIX}${value}`,
        label: value,
      });
    }
  }
  return data;
};

/** Restores saved codes, preferring the last selected provincial option for shared codes. */
export const getRuleClaimSelectKeys = (
  options: RuleClaimFieldOption[],
  values: string[],
  preferredKeys: string[]
): (string | null)[] => {
  const optionsByKey = new Map(options.map((option) => [option.value, option]));
  const keysByClaimValue = new Map<string, string>();
  for (const option of options) {
    const claimValue = getRuleClaimOptionValue(option);
    if (!keysByClaimValue.has(claimValue))
      keysByClaimValue.set(claimValue, option.value);
  }
  for (const key of preferredKeys) {
    const option = optionsByKey.get(key);
    if (option) keysByClaimValue.set(getRuleClaimOptionValue(option), key);
  }
  return [...new Set(values)].map((value) => {
    const key = keysByClaimValue.get(value);
    if (key !== undefined) return key;
    return value ? `${LEGACY_CLAIM_VALUE_PREFIX}${value}` : null;
  });
};

/** Converts selected option keys to unique claim codes, preserving unlisted saved codes. */
export const getRuleClaimValuesFromKeys = (
  options: RuleClaimFieldOption[],
  keys: string[]
): string[] => {
  const optionsByKey = new Map(options.map((option) => [option.value, option]));
  return [
    ...new Set(
      keys.map((key) => {
        const option = optionsByKey.get(key);
        return option
          ? getRuleClaimOptionValue(option)
          : key.slice(LEGACY_CLAIM_VALUE_PREFIX.length);
      })
    ),
  ];
};

type LocalizedOption<T extends string> = { value: T; label: string };

interface RuleBuilderOptions {
  localizedDayOptions: LocalizedOption<(typeof dayOptions)[number]['value']>[];
  localizedWhenOperatorOptions: LocalizedOption<
    (typeof whenOperatorOptions)[number]['value']
  >[];
  localizedTimeBasedIntervalOptions: LocalizedOption<
    (typeof timeBasedIntervalOptions)[number]['value']
  >[];
  scopeModeOptions: LocalizedOption<ScopeMode>[];
  localizedPatientAgeOperatorOptions: LocalizedOption<
    (typeof patientAgeOperatorOptions)[number]['value']
  >[];
  getDemographicOptions: (scopeMode: ScopeMode) => ClaimFieldOption[];
  getAssertionDemographicOptions: (field: AssertionField) => ClaimFieldOption[];
  assertionFieldOptions: LocalizedOption<AssertionField>[];
}

/** Localizes the shared builder choices without depending on React state. */
export const getRuleBuilderOptions = (
  translate: RuleTranslate
): RuleBuilderOptions => {
  /** Localized weekday choices shared by service-day criteria and validation checks. */
  const localizedDayOptions = dayOptions.map((option) => ({
    ...option,
    label: translate(`pages:rules.editDays.${option.value}`),
  }));

  /** Localized comparison and existence choices for When clause controls. */
  const localizedWhenOperatorOptions = whenOperatorOptions.map((option) => ({
    ...option,
    label: translate(`pages:rules.editWhenOperators.${option.value}`),
  }));

  /** Localized interval choices shown for time-based service and anesthesia updates. */
  const localizedTimeBasedIntervalOptions = timeBasedIntervalOptions.map(
    (option) => ({
      ...option,
      label: translate(`pages:rules.editIntervals.${option.value}`),
    })
  );

  /** Selectable criteria fields with labels recomputed when the locale changes. */
  const scopeModeOptions = selectableScopeModes.map((value) => ({
    value,
    label: translate(`pages:rules.createModal.scopeModes.${value}`),
  }));

  /** Localized age comparison choices reused by demographic criteria controls. */
  const localizedPatientAgeOperatorOptions = patientAgeOperatorOptions.map(
    (option) => ({
      ...option,
      label: translate(
        `pages:rules.createModal.patientAgeOperators.${option.value}`
      ),
    })
  );

  /** Localized patient sex labels paired with the codes saved in rule conditions. */
  const localizedPatientSexOptions = patientSexOptions.map((option) => ({
    ...option,
    label: translate(
      `pages:rules.createModal.patientSexOptions.${option.value}`
    ),
  }));

  /** Localized marital-status labels paired with the codes saved in rule conditions. */
  const localizedPatientMaritalStatusOptions = patientMaritalStatusOptions.map(
    (option) => ({
      ...option,
      label: translate(
        `pages:rules.createModal.patientMaritalStatusOptions.${option.value}`
      ),
    })
  );

  /** Returns province or localized demographic choices for categorical When fields. */
  const getDemographicOptions = (scopeMode: ScopeMode): ClaimFieldOption[] => {
    if (scopeMode === ScopeMode.PatientProvince) {
      return provinceOptions.map((province) => ({
        value: province,
        label: province,
      }));
    }
    if (scopeMode === ScopeMode.PatientSex) {
      return localizedPatientSexOptions;
    }
    return localizedPatientMaritalStatusOptions;
  };

  /** Maps categorical validation fields to the same choices used by demographic criteria. */
  const getAssertionDemographicOptions = (
    field: AssertionField
  ): ClaimFieldOption[] => {
    if (field === AssertionField.PatientProvince) {
      return getDemographicOptions(ScopeMode.PatientProvince);
    }
    if (field === AssertionField.PatientSex) {
      return getDemographicOptions(ScopeMode.PatientSex);
    }
    return getDemographicOptions(ScopeMode.PatientMaritalStatus);
  };

  /** Localized validation field choices exposed to legacy check editors. */
  const assertionFieldOptions = assertionFields
    .filter(
      (field) =>
        !historyCriteriaFields.some(
          (definition) => definition.assertionField === field
        )
    )
    .map((value) => ({
      value,
      label: translate(`pages:rules.createModal.assertionFields.${value}`),
    }));

  return {
    localizedDayOptions,
    localizedWhenOperatorOptions,
    localizedTimeBasedIntervalOptions,
    scopeModeOptions,
    localizedPatientAgeOperatorOptions,
    getDemographicOptions,
    getAssertionDemographicOptions,
    assertionFieldOptions,
  };
};
